/**
 * Designee Manager
 * 
 * Component for managing team members (designees) who use the admin's custom prompts.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { contentCardSx } from '../../../../../styles/standaloneLayoutTokens';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PendingIcon from '@mui/icons-material/Pending';
import { chartmindAdminService } from '../../../../../services/chartmind';

const DesigneeManager = ({
  adminId,
  designees = [],
  pendingDesignees = [],
  onDesigneeChange,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isProcessing, setIsProcessing] = useState(null);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Validate email
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Add new designee
  const handleAddDesignee = useCallback(async () => {
    if (!newEmail.trim()) return;
    
    const email = newEmail.trim().toLowerCase();
    
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (designees.includes(email)) {
      setError('This email is already a team member');
      return;
    }
    
    setIsAdding(true);
    setError('');
    
    try {
      await chartmindAdminService.addDesignee(adminId, email);
      setNewEmail('');
      onDesigneeChange?.();
    } catch (err) {
      console.error('[DesigneeManager] Error adding designee:', err);
      setError('Failed to add team member. Please try again.');
    } finally {
      setIsAdding(false);
    }
  }, [adminId, newEmail, designees, onDesigneeChange]);

  // Remove designee
  const handleRemoveDesignee = useCallback(async (email) => {
    setIsProcessing(email);
    
    try {
      await chartmindAdminService.removeDesignee(adminId, email);
      onDesigneeChange?.();
    } catch (err) {
      console.error('[DesigneeManager] Error removing designee:', err);
      setError('Failed to remove team member. Please try again.');
    } finally {
      setIsProcessing(null);
      setConfirmDelete(null);
    }
  }, [adminId, onDesigneeChange]);

  // Approve pending designee
  const handleApprove = useCallback(async (email) => {
    setIsProcessing(email);
    
    try {
      await chartmindAdminService.approvePendingDesignee(adminId, email);
      onDesigneeChange?.();
    } catch (err) {
      console.error('[DesigneeManager] Error approving designee:', err);
      setError('Failed to approve team member. Please try again.');
    } finally {
      setIsProcessing(null);
    }
  }, [adminId, onDesigneeChange]);

  // Reject pending designee
  const handleReject = useCallback(async (email) => {
    setIsProcessing(email);
    
    try {
      await chartmindAdminService.rejectPendingDesignee(adminId, email);
      onDesigneeChange?.();
    } catch (err) {
      console.error('[DesigneeManager] Error rejecting designee:', err);
      setError('Failed to reject request. Please try again.');
    } finally {
      setIsProcessing(null);
    }
  }, [adminId, onDesigneeChange]);

  return (
    <Box>
      {/* Add new member */}
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Team Member
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add a team member by their email address. They will use your custom prompts when 
          working at your facility.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Email Address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="colleague@example.com"
            fullWidth
            size="small"
            onKeyPress={(e) => e.key === 'Enter' && handleAddDesignee()}
          />
          <Button
            variant="contained"
            startIcon={isAdding ? <CircularProgress size={20} /> : <PersonAddIcon />}
            onClick={handleAddDesignee}
            disabled={isAdding || !newEmail.trim()}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Pending requests */}
      {pendingDesignees.length > 0 && (
        <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PendingIcon color="warning" />
            Pending Requests
            <Chip label={pendingDesignees.length} size="small" color="warning" />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These users requested to join your team via invite link and are awaiting approval.
          </Typography>

          <List>
            {pendingDesignees.map((pending) => (
              <ListItem 
                key={pending.email}
                sx={{ 
                  bgcolor: 'warning.lighter', 
                  borderRadius: 1, 
                  mb: 1 
                }}
              >
                <ListItemText
                  primary={pending.email}
                  secondary={`Requested: ${new Date(pending.requestedAt).toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  {isProcessing === pending.email ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <Tooltip title="Approve">
                        <IconButton
                          edge="end"
                          color="success"
                          onClick={() => handleApprove(pending.email)}
                        >
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleReject(pending.email)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Current team members */}
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Team Members
          <Chip label={designees.length} size="small" color="primary" />
        </Typography>
        
        {designees.length === 0 ? (
          <Alert severity="info">
            No team members yet. Add members above or share an invite link.
          </Alert>
        ) : (
          <List>
            {designees.map((email) => (
              <ListItem 
                key={email}
                sx={{ 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  mb: 1 
                }}
              >
                <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText primary={email} />
                <ListItemSecondaryAction>
                  {isProcessing === email ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Tooltip title="Remove from team">
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => setConfirmDelete(email)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Confirm delete dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove Team Member?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{confirmDelete}</strong> from your team?
            They will no longer use your custom prompts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button 
            onClick={() => handleRemoveDesignee(confirmDelete)} 
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DesigneeManager;
